// Live room lighting from the room variables (contract section 7).
//
// The baked colour already contains sun + pots, so the variables are approximated by a grade:
//   baked * (SUN_W * sun + POTS_W * pots * pots_rgb + accent wash) * tint
// where the accent wash falls off with the distance from the accent point (the centre panel). The split
// SUN_W / POTS_W is a guess, not measured from the bake. (The layered-lighting path that read separate
// lightmaps per light group was dropped with the feature; contracts section 7.)
//
// All strengths and colours are uniforms shared by every patched material (one object each), so a change
// only writes uniform values: no material is recompiled. One program per material kind (reflective or not).

import { Color, Material, Vector3, type MeshBasicMaterial } from 'three';
import type { RoomVars } from '@lr3d/shared';
import type { LoadedScene } from '../scene/loader';

const SUN_W = 0.65;
const POTS_W = 0.35;
/** Accent wash: gain at the accent point and falloff radius (m). */
const ACCENT_GAIN = 0.9;
const ACCENT_R = 1.1;

const shared = {
  lrSun: { value: 1 },
  /** pots strength x pots colour (linear) */
  lrPots: { value: new Vector3(1, 1, 1) },
  /** accent strength x accent colour (linear) */
  lrAccent: { value: new Vector3(0, 0, 0) },
  lrAccentPos: { value: new Vector3(3.5, 1.3, -4.8) },
};
const WHITE = { value: new Vector3(1, 1, 1) };

const _c = new Color();
const rgb = (hex: string, out: Vector3, k = 1) => {
  _c.set(hex); // sRGB hex -> linear working space
  return out.set(_c.r * k, _c.g * k, _c.b * k);
};

const VERT_PARS = /* glsl */ `varying vec3 vLrWorld;\n`;
const VERT = /* glsl */ `#include <project_vertex>
vLrWorld = ( modelMatrix * vec4( transformed, 1.0 ) ).xyz;`;
const FRAG_PARS = /* glsl */ `varying vec3 vLrWorld;
uniform float lrSun;
uniform vec3 lrPots;
uniform vec3 lrAccent;
uniform vec3 lrAccentPos;
uniform vec3 lrTint;
`;
const FRAG = /* glsl */ `vec3 outgoingLight = reflectedLight.indirectDiffuse;
{
  vec3 lrD = vLrWorld - lrAccentPos;
  float lrFall = ${ACCENT_GAIN.toFixed(3)} / ( 1.0 + dot( lrD, lrD ) / ${(ACCENT_R * ACCENT_R).toFixed(4)} );
  outgoingLight *= ( ${SUN_W.toFixed(3)} * lrSun + ${POTS_W.toFixed(3)} * lrPots + lrAccent * lrFall ) * lrTint;
}`;
const OUT_LINE = 'vec3 outgoingLight = reflectedLight.indirectDiffuse;';

/** Chain a shader patch after the material's existing onBeforeCompile (the reflection layer). */
function chain(mat: MeshBasicMaterial, key: string, patch: (shader: { vertexShader: string; fragmentShader: string; uniforms: Record<string, { value: unknown }> }) => void): void {
  const prev = mat.onBeforeCompile;
  const prevKey = mat.customProgramCacheKey === Material.prototype.customProgramCacheKey ? '' : mat.customProgramCacheKey();
  mat.onBeforeCompile = (shader, renderer) => {
    prev.call(mat, shader, renderer);
    patch(shader);
  };
  mat.customProgramCacheKey = () => `${prevKey}|${key}`;
  mat.needsUpdate = true;
}

export class RoomShading {
  /** tint id -> uniform (shared by that id's materials) */
  private tints = new Map<string, { value: Vector3 }>();
  private patched = new WeakSet<Material>();
  private vars: RoomVars | null = null;

  /** Point the accent wash at (world). */
  setAccentPoint(p: Vector3): void {
    shared.lrAccentPos.value.copy(p);
  }

  private tintUniform(id: string): { value: Vector3 } {
    let u = this.tints.get(id);
    if (!u) {
      u = { value: new Vector3(1, 1, 1) };
      this.tints.set(id, u);
      this.applyTint(id, u);
    }
    return u;
  }

  private applyTint(id: string, u: { value: Vector3 }): void {
    const hex = (this.vars as unknown as Record<string, unknown> | null)?.[`${id}_tint`];
    if (typeof hex === 'string') rgb(hex, u.value);
    else u.value.set(1, 1, 1);
  }

  /** Patch a loaded tier's room materials (before its programs are compiled). */
  prepare(s: LoadedScene): void {
    const tintOf = new Map<Material, string>();
    for (const [id, meshes] of s.tintables) for (const m of meshes) tintOf.set(m.material as Material, id);
    for (const mat of s.roomMaterials) {
      if (this.patched.has(mat)) continue;
      this.patched.add(mat);
      const tid = tintOf.get(mat);
      this.patch(mat, tid ? this.tintUniform(tid) : WHITE);
    }
  }

  /**
   * Grade one material like the room (artwork frames and pictures: they are not in the bake, so their colour
   * is the wall's light sampled at load, and the room variables apply on top the same way).
   */
  patch(mat: MeshBasicMaterial, lrTint: { value: Vector3 } = WHITE): void {
    this.patched.add(mat);
    chain(mat, 'lr3d-grade-v1', (shader) => {
      Object.assign(shader.uniforms, shared, { lrTint });
      shader.vertexShader = VERT_PARS + shader.vertexShader.replace('#include <project_vertex>', VERT);
      shader.fragmentShader = FRAG_PARS + shader.fragmentShader.replace(OUT_LINE, FRAG);
    });
  }

  /** Run `fn` with the grade at neutral (the scene defaults, i.e. the plain bake: sun 1, pots white 1, no accent, no tints), then restore. */
  neutral<T>(fn: () => T): T {
    shared.lrSun.value = 1;
    shared.lrPots.value.set(1, 1, 1);
    shared.lrAccent.value.set(0, 0, 0);
    for (const u of this.tints.values()) u.value.set(1, 1, 1);
    try {
      return fn();
    } finally {
      if (this.vars) this.apply(this.vars);
    }
  }

  /** Write the variables into the shared uniforms (no recompiles). */
  apply(vars: RoomVars): void {
    this.vars = vars;
    shared.lrSun.value = vars.sun;
    rgb(vars.pots_color, shared.lrPots.value, vars.pots);
    rgb(vars.accent_color, shared.lrAccent.value, vars.accent);
    for (const [id, u] of this.tints) this.applyTint(id, u);
  }
}
